import React from "react";
import { MapPin, MessageSquare, FileCheck2, Sparkles, ShieldCheck, Flame } from "lucide-react";
import { BUSINESS } from "../../data/business";

// Lean replacement for the old "Why Choose" card grid. Same six promises but
// rendered as a single horizontal feature strip:
//   • desktop: 3-up grid, two rows, big icon + title + one-line description
//   • mobile:  horizontal-snap carousel showing icon + title only (no body text)
// We override the WHY_CHOOSE description text here to keep it short and scannable.
const WHY = [
  { title: "Local to Baltimore", icon: MapPin, color: "#54616E",
    blurb: "Based in Baltimore — we work this metro every week." },
  { title: "Clear Communication", icon: MessageSquare, color: "#1A1D21",
    blurb: "Plain-English explanations, every option on the table." },
  { title: "Upfront Estimates", icon: FileCheck2, color: "#E31D2B",
    blurb: "You see the price before any wrench turns." },
  { title: "Clean Workmanship", icon: Sparkles, color: "#54616E",
    blurb: "Shoe covers, drop cloths, leave it cleaner than we found it." },
  { title: "Licensed & Insured", icon: ShieldCheck, color: "#1A1D21",
    blurb: "Local plumbing contractor with full insurance." },
  { title: "Drain & Sewer Pros", icon: Flame, color: "#E31D2B",
    blurb: "Camera, jetting, and snaking — drains are what we do." },
];

function FeatureTile({ item, compact = false }) {
  const Icon = item.icon;
  return (
    <div
      className={
        compact
          ? "shrink-0 w-[180px] sm:w-[200px] snap-start abc-card p-5 text-center"
          : "abc-card p-6"
      }
      data-testid={`why-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <span
        className={`inline-flex items-center justify-center rounded-2xl ${compact ? "w-12 h-12 mx-auto" : "w-12 h-12"}`}
        style={{ background: `${item.color}14`, color: item.color }}
      >
        <Icon className="w-6 h-6" strokeWidth={2} />
      </span>
      <h3 className={`font-display font-bold text-[#1A1D21] mt-4 ${compact ? "text-[15px]" : "text-lg"}`}>
        {item.title}
      </h3>
      {!compact && (
        <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{item.blurb}</p>
      )}
    </div>
  );
}

export default function WhyChooseSection() {
  return (
    <section className="py-14 md:py-20" data-testid="home-why">
      <div className="abc-container">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-widest text-[#54616E] mb-3">Why Drain Sights</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1A1D21] leading-tight">
            The plumbers your neighbors <span className="text-[#E31D2B]">actually recommend</span>.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            Small focused team. Real people answering the phone 24/7. Fair quotes, clean work, and a number that always reaches a human.
          </p>
        </div>

        {/* Desktop: 3-up grid, full descriptions */}
        <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {WHY.map((item) => <FeatureTile key={item.title} item={item} />)}
        </div>

        {/* Mobile: horizontal-snap carousel with just icon + title */}
        <div
          className="sm:hidden mt-8 -mx-5 px-5 flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 abc-no-scrollbar"
          data-testid="why-mobile-carousel"
        >
          {WHY.map((item) => <FeatureTile key={item.title} item={item} compact />)}
        </div>

        <p className="text-xs text-slate-500 mt-6">
          Questions? Call <a href={BUSINESS.phoneHref} className="text-[#54616E] hover:text-[#1A1D21] font-semibold">{BUSINESS.phone}</a> — a real person answers 24/7, day or night.
        </p>
      </div>
    </section>
  );
}
